import React, { forwardRef } from 'react';
import ReportPaper from './report/ReportPaper';
import paper from './report/ReportPaper.module.css';
import styles from './BodyMeasurementsReportTemplate.module.css';
import { BODY_MEASURES, BODY_MEASURE_GROUP, BODY_MEASURE_GROUPS } from '../../pages/appointment/modules/bodyMeasures';
import { buildIndicators } from '../../pages/appointment/components/bodyMeasurements/BodyIndicatorCards';
import { getSkinfoldProtocol } from '../../pages/appointment/components/bodyMeasurements/bodyComposition';
import {
  computeDelta,
  formatDate,
  formatDelta,
  formatMeasure,
  formatNumber,
  getMeasureValue,
  getValue,
  historyGroups as buildHistoryGroups,
  sortByDateDesc,
} from '../../pages/appointment/components/bodyMeasurements/bodyMeasurementsMath';

// Colunas do histórico no papel: as avaliações mais recentes
const MAX_HISTORY_COLUMNS = 6;

const missing = <span className={paper.muted}>—</span>;

/** Subtítulo e data da assinatura da avaliação (`job` = { history, current, reference, variant }). */
export function bodyMeasurementsReportMeta({ current, reference }) {
  const date = formatDate(current && current.measuredAt);
  const referenceDate = formatDate(reference && reference.measuredAt);
  return {
    subtitle: `Avaliação de ${date}${referenceDate ? ` · variação em relação a ${referenceDate}` : ''}`,
    date,
  };
}

/**
 * Miolo do PDF: indicadores (peso, altura, IMC classificado, gordura digitada
 * e pelas dobras, relações cintura-quadril e cintura-estatura) com a
 * variação, as demais medidas da avaliação mais recente (circunferências,
 * dobras, diâmetros) lado a lado com a referência, observações e o histórico
 * das últimas avaliações com os indicadores calculados.
 *
 * `history`: todas as avaliações; `current`: a avaliação do relatório;
 * `reference`: avaliação de comparação (ou null); `variant`: silhueta e sexo;
 * `birthday`: nascimento do paciente (idade das equações das dobras).
 */
export const BodyMeasurementsReportBody = ({ history, current, reference, variant, birthday }) => {
  const list = Array.isArray(history) ? history : [];
  const subject = { variant, birthday };
  const indicators = current ? buildIndicators(list, current, reference, variant, birthday) : [];
  const date = formatDate(current && current.measuredAt);
  const referenceDate = formatDate(reference && reference.measuredAt);
  const protocol = getSkinfoldProtocol(current && current.skinfoldProtocol);

  const measureGroups = BODY_MEASURE_GROUPS
    .filter((group) => group.key !== BODY_MEASURE_GROUP.INDICATORS)
    .map((group) => ({
      ...group,
      measures: BODY_MEASURES
        .filter((measure) => measure.group === group.key)
        .filter((measure) => getValue(current, measure.key) !== null || (reference && getValue(reference, measure.key) !== null)),
    }))
    .filter((group) => group.measures.length > 0);

  const columns = sortByDateDesc(list).slice(0, MAX_HISTORY_COLUMNS);
  const historyGroups = buildHistoryGroups(list, columns, subject);

  return (
    <>
      {indicators.length > 0 && (
        <div className={`${paper.section} ${styles.tiles}`}>
          {indicators.map((item) => (
            <div key={item.key} className={styles.tile}>
              <span className={styles.tileLabel}>{item.label}</span>
              <span className={styles.tileValue}>{item.value}</span>
              {item.delta && <span className={styles.tileDelta}>{item.delta}</span>}
              {item.ruler && <span className={styles.tileNote}>{item.ruler.caption} · {item.ruler.source}</span>}
              {item.note && <span className={styles.tileNote}>{item.note}</span>}
              {(item.details || []).map((detail) => <span key={detail} className={styles.tileNote}>{detail}</span>)}
            </div>
          ))}
        </div>
      )}

      {measureGroups.length > 0 && (
        <div className={paper.section}>
          <span className={paper.sectionTitle}>Medidas</span>
          <table className={paper.table}>
            <thead>
              <tr>
                <th>Medida</th>
                <th className={paper.num}>{date}</th>
                {reference && <th className={paper.num}>{referenceDate}</th>}
                {reference && <th className={paper.num}>Variação</th>}
              </tr>
            </thead>
            <tbody>
              {measureGroups.map((group) => (
                <React.Fragment key={group.key}>
                  <tr className={styles.groupRow}>
                    <td colSpan={reference ? 4 : 2}>
                      {group.label}
                      {group.key === BODY_MEASURE_GROUP.SKINFOLDS && protocol ? ` · ${protocol.label}` : ''}
                    </td>
                  </tr>
                  {group.measures.map((measure) => {
                    const value = getValue(current, measure.key);
                    const referenceValue = reference ? getValue(reference, measure.key) : null;
                    const delta = formatDelta(computeDelta(value, referenceValue), measure.key);
                    return (
                      <tr key={measure.key}>
                        <td>{measure.label}</td>
                        <td className={paper.num}>{value === null ? missing : formatMeasure(value, measure.key)}</td>
                        {reference && <td className={paper.num}>{referenceValue === null ? missing : formatMeasure(referenceValue, measure.key)}</td>}
                        {reference && <td className={paper.num}>{delta || missing}</td>}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {current && current.notes && (
        <div className={paper.noteBox}>
          <span className={paper.sectionTitle}>Observações</span>
          <div className={paper.noteText}>{current.notes}</div>
        </div>
      )}

      {columns.length > 1 && historyGroups.length > 0 && (
        <div className={paper.section}>
          <span className={paper.sectionTitle}>
            Histórico{list.length > columns.length ? ` (${columns.length} avaliações mais recentes de ${list.length})` : ''}
          </span>
          <table className={paper.table}>
            <thead>
              <tr>
                <th>Medida</th>
                {columns.map((assessment) => (
                  <th key={assessment.id} className={paper.num}>{formatDate(assessment.measuredAt)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {historyGroups.map((group) => (
                <React.Fragment key={group.key}>
                  <tr className={styles.groupRow}>
                    <td colSpan={columns.length + 1}>{group.label}</td>
                  </tr>
                  {group.measures.map((measure) => (
                    <tr key={measure.key}>
                      <td>{measure.label} <span className={styles.unit}>{measure.unit}</span></td>
                      {columns.map((assessment) => {
                        const text = formatNumber(getMeasureValue(list, assessment, measure.key, subject), measure.decimals);
                        return <td key={assessment.id} className={paper.num}>{text === null ? missing : text}</td>;
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

/** PDF das medidas corporais para o paciente: papel + miolo. */
const BodyMeasurementsReportTemplate = forwardRef(({ companyData, professional, patientName, ...job }, ref) => {
  const meta = bodyMeasurementsReportMeta(job);
  return (
    <ReportPaper
      ref={ref}
      companyData={companyData}
      professional={professional}
      patientName={patientName}
      title="Medidas corporais"
      subtitle={meta.subtitle}
      date={meta.date}
    >
      <BodyMeasurementsReportBody {...job} />
    </ReportPaper>
  );
});

BodyMeasurementsReportTemplate.displayName = 'BodyMeasurementsReportTemplate';

export default BodyMeasurementsReportTemplate;
